import React, { FC, useRef, useEffect, useState, useContext, useCallback, memo } from 'react'
import { Modal, ScreenContext, BottomSheet } from 'react-components'
import AmenitiesPopupContent from './content'
import { FormattedRoomsResult } from '../../../../models/property-details-page/rooms-model'
import { FormattedSuitableFeatures } from '../../../../models/property-details-page/suitables-model'
import isEmpty from 'lodash/isEmpty'

export interface AmenitiesPopupProps {
    isOpen: boolean
    onClose: () => void
    rooms: FormattedRoomsResult[]
    extendedFeatures: FormattedSuitableFeatures | null
}

const AmenitiesPopup: FC<AmenitiesPopupProps> = ({ isOpen, onClose, rooms, extendedFeatures }) => {
    const [activeRoom, setActiveRoom] = useState<number>(0)
    const tabBarRef = useRef<HTMLDivElement | null>(null)
    const tabRefs = useRef<(HTMLButtonElement | null)[]>([])
    const roomRefs = useRef<(HTMLDivElement | null)[]>([])
    const modalContentRef = useRef<HTMLDivElement | null>(null)
    const { isMobile } = useContext(ScreenContext)

    const handleScroll = useCallback(() => {
        if (!modalContentRef.current || isEmpty(rooms)) {
            return
        }

        const scrollPositions = roomRefs.current.map(ref =>
            ref ? ref.offsetTop - modalContentRef.current!.scrollTop - 10 : Number.POSITIVE_INFINITY,
        )

        const currentIndex = scrollPositions.findIndex(
            (pos, index) => scrollPositions[index + 1] && pos >= 0 && pos < scrollPositions[index + 1],
        )

        setActiveRoom(currentIndex >= 0 ? currentIndex : rooms.length - 1)
    }, [rooms])

    const handleTabClick = useCallback(
        (index: number) => {
            const container = modalContentRef.current
            if (container && roomRefs.current[index]) {
                const targetOffset = roomRefs.current[index]!.offsetTop - container.offsetTop - 10
                container.scrollTo({ top: targetOffset, behavior: 'smooth' })
            }
            setActiveRoom(index)
        },
        [setActiveRoom],
    )

    const setContentRef = useCallback(
        (instance: HTMLDivElement | null) => {
            modalContentRef.current = instance
            if (instance) {
                instance.addEventListener('scroll', handleScroll)
            }
        },
        [handleScroll],
    )

    useEffect(() => {
        const activeTab = tabRefs.current[activeRoom]
        const tabBar = tabBarRef.current

        if (activeTab && tabBar) {
            const tabLeft = activeTab.offsetLeft
            const tabWidth = activeTab.offsetWidth
            const tabBarWidth = tabBar.offsetWidth

            tabBar.scrollTo({
                left: tabLeft - tabBarWidth / 2 + tabWidth / 2,
                behavior: 'smooth',
            })
        }
    }, [activeRoom])

    if (isEmpty(rooms) && !extendedFeatures) {
        return null
    }

    if (isMobile) {
        return (
            <BottomSheet
                rootElementSelector="#__next"
                header={null}
                isOpen={isOpen}
                onClose={onClose}
                contentRef={setContentRef}
                content={
                    <AmenitiesPopupContent
                        tabBarRef={tabBarRef}
                        rooms={rooms}
                        extendedFeatures={extendedFeatures}
                        activeRoom={activeRoom}
                        handleTabClick={handleTabClick}
                        tabRefs={tabRefs}
                        roomRefs={roomRefs}
                    />
                }
            />
        )
    }

    return (
        <Modal isOpen={isOpen} onRequestClose={onClose} contentRef={setContentRef} preventScroll>
            <AmenitiesPopupContent
                tabBarRef={tabBarRef}
                rooms={rooms}
                extendedFeatures={extendedFeatures}
                activeRoom={activeRoom}
                handleTabClick={handleTabClick}
                tabRefs={tabRefs}
                roomRefs={roomRefs}
            />
        </Modal>
    )
}

export default memo(AmenitiesPopup)
